// Kundekortet (/customer-details/:id), i startsidens stil (planer/ny-kundesamhandling-plan.md):
// kunden øverst, varsel om aktiv booking, de samme knappene som før, og så to samlede lister i
// stedet for én tabell per type: «Aktive nå» og «Avsluttet» (kan filtreres på type). Hver rad er
// merket med typen (booking, service, trykk, ordre, pristilbud, dekkhotell, sykkelhotell).
// SMS og e-post står i én tabell der bare siste melding vises til den foldes ut.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import SmsStatusMerke from "./components/SmsStatusMerke";
import Servicepaminnelse from "./components/Servicepaminnelse";
import { useParams, useNavigate, Link } from 'react-router-dom';
import { format, parse, isValid } from 'date-fns';
import Cookies from 'js-cookie';
import { apneButikkmodus } from './utils/butikkmodus';
import { SETT_LABEL, settTekst, stedTekst } from './constants/dekkhotell';
import { STATUS_KORT as SYKKELHOTELL_STATUS, sykkelTekst } from './constants/sykkelhotell';
import { FARGER, FORSIDE } from './ny-kundesamhandling/handlinger';
import Ikon from './ny-kundesamhandling/Ikon';
import { ordreStatusTekst } from './ny-kundesamhandling/Skjema';
import { useErLeder } from './employeeAccess';

// ─────────────────────────────────────────────────────────────

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

const normalizeKategori = (...cands) => {
  for (const v of cands) {
    if (v == null) continue;

    if (Array.isArray(v)) {
      const first = v.find(Boolean);
      if (first) return String(first);
      continue;
    }

    if (typeof v === 'object') {
      const fromObj = v.navn || v.title || v.label || v.kategori || v.category;
      if (fromObj) return String(fromObj);
      continue;
    }

    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
};

const normalizePhone = (value) => {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');

  if (digits.length === 8) return digits;
  if (digits.length === 10 && digits.startsWith('47')) return digits.slice(2);
  if (digits.length === 12 && digits.startsWith('0047')) return digits.slice(4);

  return digits;
};

const parseCustomDateString = (dateString) => {
  if (!dateString) return null;
  const parsedDate = parse(dateString, 'd.M.yyyy, HH:mm:ss', new Date());
  return isValid(parsedDate) ? parsedDate : null;
};

// Gamle ordrer og tjenester har dato som «d.M.yyyy, HH:mm:ss»; resten er ISO.
const tilDato = (v) => {
  if (!v) return null;
  if (typeof v === 'string') {
    const custom = parseCustomDateString(v);
    if (custom) return custom;
  }
  const d = new Date(v);
  return isValid(d) ? d : null;
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'Ukjent dato';
  const parsed = new Date(dateString);
  return isValid(parsed) ? format(parsed, 'dd.MM.yyyy HH:mm') : 'Ukjent dato';
};

const kortDato = (d) => (d ? format(d, 'd.M.yyyy') : '');

function formatNOK(n) {
  const val = Number(n) || 0;
  return new Intl.NumberFormat('no-NO', {
    style: 'currency',
    currency: 'NOK',
    maximumFractionDigits: 0,
  }).format(val);
}

function formatMinutes(min) {
  const m = Math.max(0, Number(min) || 0);
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h && r) return `${h} t ${r} min`;
  if (h) return `${h} t`;
  return `${r} min`;
}

function parseDateSafe(d) {
  const dt = d ? new Date(d) : null;
  return dt && !isNaN(+dt) ? dt : null;
}

function fmtDateShort(d) {
  const dt = parseDateSafe(d);
  if (!dt) return '';
  return dt.toLocaleDateString('no-NO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function fbCount(tjeneste) {
  const fb = Array.isArray(tjeneste?.foranBak)
    ? tjeneste.foranBak.map((v) => String(v).toLowerCase())
    : [];
  const hasForan = fb.includes('foran');
  const hasBak = fb.includes('bak');
  const c = (hasForan ? 1 : 0) + (hasBak ? 1 : 0);
  return c || 1;
}

function isActiveBookingStatus(status) {
  const s = String(status || '').trim().toLowerCase();

  return [
    'booking',
    'aktiv',
    'active',
    'pending',
    'reservert',
    'reserved',
    'booket',
  ].includes(s);
}

const storForbokstav = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : '');

// Typene i de samlede listene: navn, ikon og farge på merket. Fargene er de samme som på
// startsiden (verksted grønn, trykk lilla, kunde blå, betaling gul, lager turkis).
const TYPER = {
  booking: { navn: 'Booking', ikon: 'kalender', merke: 'bg-amber-100 text-amber-900 ring-amber-300' },
  service: { navn: 'Service', ikon: 'skiftenokkel', merke: 'bg-emerald-100 text-emerald-900 ring-emerald-300' },
  trykk: { navn: 'Trykk', ikon: 'genser', merke: 'bg-violet-100 text-violet-900 ring-violet-300' },
  ordre: { navn: 'Ordre', ikon: 'kvittering', merke: 'bg-sky-100 text-sky-900 ring-sky-300' },
  tilbud: { navn: 'Pristilbud', ikon: 'dokument', merke: 'bg-orange-100 text-orange-900 ring-orange-300' },
  dekkhotell: { navn: 'Dekkhotell', ikon: 'dekk', merke: 'bg-teal-100 text-teal-900 ring-teal-300' },
  sykkelhotell: { navn: 'Sykkelhotell', ikon: 'hotell', merke: 'bg-teal-100 text-teal-900 ring-teal-300' },
};

const KATEGORI_NAVN = { sykkel: 'Sykkel', ski: 'Ski', skoyteslip: 'Skøyteslip', skøyteslip: 'Skøyteslip' };

// Gamle tjenester (Service-modellen) åpnes på sin egen side etter type.
function gammelTjenesteSti(service) {
  if (service.servicetype === 'Sykkelservice') return `/service-details-bike/${service._id}`;
  if (service.servicetype === 'Skiservice') return `/service-details-ski/${service._id}`;
  if (service.servicetype === 'Skøyteslip') return `/service-details-skate/${service._id}`;
  if (service.servicetype === 'Tekstiltrykking') return `/service-details-club/${service._id}`;
  return `/service-details/${service._id}`;
}

function TypeMerke({ type }) {
  const t = TYPER[type];
  return (
    <span className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-sm font-semibold ring-1 ${t.merke}`}>
      <Ikon navn={t.ikon} className="w-4 h-4" />
      {t.navn}
    </span>
  );
}

// Én rad i «Aktive nå» eller «Avsluttet». Hele raden er lenken.
function SakRad({ sak }) {
  return (
    <li>
      <Link
        to={sak.til}
        className="group flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-1.5 min-h-[2.75rem] transition hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-inset focus-visible:ring-indigo-600/50"
      >
        <TypeMerke type={sak.type} />
        <span className="min-w-0 flex-1 basis-48">
          <span className="block font-semibold text-slate-900 [overflow-wrap:anywhere]">{sak.tittel}</span>
          {sak.detaljer && <span className="block text-sm text-slate-600 [overflow-wrap:anywhere] line-clamp-2">{sak.detaljer}</span>}
        </span>
        <span className="flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          {sak.status && <span className="rounded-full bg-slate-100 px-2.5 py-0.5 font-medium text-slate-800">{sak.status}</span>}
          {sak.dato && <span className="text-slate-600">{kortDato(sak.dato)}</span>}
          <Ikon navn="pil" className="w-5 h-5 text-slate-500 transition group-hover:translate-x-0.5 group-hover:text-indigo-700" />
        </span>
      </Link>
    </li>
  );
}

function Seksjon({ id, tittel, antall, children, hoyre }) {
  return (
    <section aria-labelledby={id} className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-2">
        <h2 id={id} className="text-lg font-bold text-slate-900">{tittel}</h2>
        {antall != null && (
          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-sm font-semibold text-slate-700">{antall}</span>
        )}
        {hoyre && <div className="ml-auto">{hoyre}</div>}
      </div>
      {children}
    </section>
  );
}

function MeldingType({ type }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${type === 'SMS' ? 'bg-sky-100 text-sky-900' : 'bg-violet-100 text-violet-900'}`}>
      <Ikon navn={type === 'SMS' ? 'melding' : 'send'} className="w-4 h-4" />
      {type}
    </span>
  );
}

function Tom({ children }) {
  return <p className="rounded-xl border border-dashed border-slate-300 px-3 py-3 text-slate-600">{children}</p>;
}

// Knappene på kundekortet, i stil med «Mer»-knappene på startsiden.
function Handling({ ikon, farge, tittel, to, state, onClick, disabled }) {
  const f = FARGER[farge] || FARGER.info;
  const cls = `flex h-full w-full items-center gap-2 rounded-xl border px-2.5 py-1.5 min-h-[2.5rem] text-left transition active:scale-[0.98] touch-manipulation focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50 disabled:opacity-60 ${f.flate}`;
  const innhold = (
    <>
      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${f.ikon}`}>
        <Ikon navn={ikon} className="w-5 h-5" />
      </span>
      <span className="min-w-0 text-sm font-semibold leading-tight text-slate-900">{tittel}</span>
    </>
  );
  return to ? (
    <Link to={to} state={state} className={cls}>{innhold}</Link>
  ) : (
    <button type="button" onClick={onClick} disabled={disabled} className={cls}>{innhold}</button>
  );
}

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

function CustomerDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const butikkid = Cookies.get('butikkid') || null;

  const [customer, setCustomer] = useState(null);

  const [orders, setOrders] = useState([]);
  const [legacyServices, setLegacyServices] = useState([]);
  const [workshopServices, setWorkshopServices] = useState([]);
  const [smsArchive, setSmsArchive] = useState([]);
  const [emailArchive, setEmailArchive] = useState([]);
  const [offers, setOffers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [dekkhotell, setDekkhotell] = useState([]);
  const [sykkelhotell, setSykkelhotell] = useState([]);

  const [bikes, setBikes] = useState([]);

  const [visAlleMeldinger, setVisAlleMeldinger] = useState(false);
  const [avsluttetType, setAvsluttetType] = useState('alle');
  const [visAntallAvsluttet, setVisAntallAvsluttet] = useState(10);

  const [customerLoading, setCustomerLoading] = useState(true);
  const [error, setError] = useState('');

  const [creatingOffer, setCreatingOffer] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Bare leder sletter kunder (Anders, 26.10). Skjuler knappen, ikke tilgangskontroll.
  const erLeder = useErLeder();
  // «Send GDPR-data til kunde»: null | 'sporsmal' | 'sender' | { til, kanal } | { feil }
  const [innsyn, setInnsyn] = useState(null);

  const [sectionLoading, setSectionLoading] = useState({
    sms: false,
    email: false,
    orders: false,
    bookings: false,
    workshopServices: false,
    legacyServices: false,
    offers: false,
    hotell: false,
  });

  const setOneSectionLoading = (key, value) => {
    setSectionLoading((prev) => ({ ...prev, [key]: value }));
  };

  const activeBookingBookings = useMemo(() => {
    return bookings.filter((b) => isActiveBookingStatus(b.status));
  }, [bookings]);

  const goToBmod = () => apneButikkmodus(customer);

  const fetchLegacyServices = useCallback(
    async (customerNumber) => {
      setOneSectionLoading('legacyServices', true);
      try {
        const response = await fetch(`${API_BASE_URL}/services`);

        if (response.ok) {
          let servicesData = await response.json();

          servicesData = servicesData.filter(
            (s) => String(s.kundeid || '') === String(customerNumber || '')
          );

          if (butikkid) {
            servicesData = servicesData.filter(
              (s) => s.butikkid === parseInt(butikkid, 10)
            );
          }

          servicesData.sort(
            (a, b) =>
              new Date(b.registrertDato || 0) - new Date(a.registrertDato || 0)
          );

          setLegacyServices(servicesData);
        } else {
          setLegacyServices([]);
        }
      } catch (error) {
        console.error('Feil ved henting av gamle tjenester:', error);
        setLegacyServices([]);
      } finally {
        setOneSectionLoading('legacyServices', false);
      }
    },
    [butikkid]
  );

  const createOffer = async () => {
    setCreatingOffer(true);
    try {
      const r = await fetch(`${API_BASE_URL}/offers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          kundeid: customer._id,
          ansatt: Cookies.get("selectedEmployee") || "",
          // Butikken den ansatte er i; kundens kontaktinfo henter serveren fra kundekortet.
          butikkid: Number(Cookies.get("butikkid")) === 2 ? 2 : 1,
          produkter: [],
        }),
      });
      if (!r.ok) throw new Error("Feil ved oppretting av tilbud");
      const newOffer = await r.json();
      navigate(`/offer-details/${newOffer._id}`);
    } catch (err) {
      console.error(err);
    } finally {
      setCreatingOffer(false);
    }
  };

  const fetchBookings = useCallback(
    async (customerData) => {
      setOneSectionLoading('bookings', true);

      try {
        const response = await fetch(`${API_BASE_URL}/bookings`);
        if (!response.ok) throw new Error('Kunne ikke hente bookinger');

        const raw = await response.json();

        const customerId = String(customerData?._id || '').trim();
        const customerPhone = normalizePhone(customerData?.phoneNumber || '');
        const customerEmail = String(customerData?.email || '').trim().toLowerCase();
        const customerFirstName = String(customerData?.firstName || '').trim().toLowerCase();
        const customerLastName = String(customerData?.lastName || '').trim().toLowerCase();

        let filteredBookings = (raw || []).filter((b) => {
          const bookingCustomerId = String(
            b.customer?._id || b.customerId || ''
          ).trim();

          const bookingPhone = normalizePhone(
            b.customer?.phoneNumber || b.phoneNumber || ''
          );

          const bookingEmail = String(
            b.customer?.email || b.email || ''
          ).trim().toLowerCase();

          const bookingFirstName = String(
            b.customer?.firstName || ''
          ).trim().toLowerCase();

          const bookingLastName = String(
            b.customer?.lastName || ''
          ).trim().toLowerCase();

          const matchById =
            !!customerId &&
            !!bookingCustomerId &&
            bookingCustomerId === customerId;

          const matchByPhone =
            !!customerPhone &&
            !!bookingPhone &&
            bookingPhone === customerPhone;

          const matchByEmail =
            !!customerEmail &&
            !!bookingEmail &&
            bookingEmail === customerEmail;

          const matchByName =
            !!customerFirstName &&
            !!customerLastName &&
            bookingFirstName === customerFirstName &&
            bookingLastName === customerLastName;

          return matchById || matchByPhone || matchByEmail || matchByName;
        });

        if (butikkid) {
          filteredBookings = filteredBookings.filter((b) => {
            const items = Array.isArray(b.items) ? b.items : [];
            const butikkFraBooking =
              String(items[0]?.butikk || b.butikk || '').toLowerCase();

            if (String(butikkid) === '1') return butikkFraBooking.includes('røyken');
            if (String(butikkid) === '2') return butikkFraBooking.includes('slemmestad');

            return true;
          });
        }

        const normalized = filteredBookings.map((b) => {
          let totalPrice = 0;
          let totalMinutes = 0;
          let latestFinish = null;
          let storeName = '';

          const items = Array.isArray(b.items) ? b.items : [];

          for (const prod of items) {
            if (!storeName && prod?.butikk) {
              storeName = String(prod.butikk);
            }

            const ferdig = parseDateSafe(prod?.forventetFerdigDato);
            if (ferdig && (!latestFinish || ferdig > latestFinish)) {
              latestFinish = ferdig;
            }

            const tlist = Array.isArray(prod?.items)
              ? prod.items
              : Array.isArray(prod?.tjenester)
              ? prod.tjenester
              : [];

            for (const t of tlist) {
              if (typeof t?.pris === 'number') {
                totalPrice += (Number(t.pris) || 0) * fbCount(t);
              }
              if (typeof t?.tidsbruk === 'number') {
                totalMinutes += Math.max(0, Number(t.tidsbruk) || 0);
              }
            }
          }

          return {
            id: b._id,
            status: b.status || 'booking',
            createdAt: b.createdAt || null,
            createdAtText: b.createdAt ? formatDateTime(b.createdAt) : 'Ukjent dato',
            firstName: b.customer?.firstName || '',
            lastName: b.customer?.lastName || '',
            phone: b.customer?.phoneNumber || b.phoneNumber || '',
            storeName: storeName || b.butikk || '—',
            items,
            totalItems: items.length,
            totalPrice,
            totalMinutes,
            estimatedFinish: latestFinish ? fmtDateShort(latestFinish) : '',
            estimatedFinishRaw: latestFinish ? latestFinish.toISOString() : null,
          };
        });

        normalized.sort(
          (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
        );

        setBookings(normalized);
      } catch (error) {
        console.error('Feil ved henting av bookinger:', error);
        setBookings([]);
      } finally {
        setOneSectionLoading('bookings', false);
      }
    },
    [butikkid]
  );

  const fetchWorkshopServices = useCallback(
    async (customerData) => {
      setOneSectionLoading('workshopServices', true);

      try {
        const response = await fetch(`${API_BASE_URL}/workshop-services`);
        if (!response.ok) throw new Error('Kunne ikke hente workshop-services');

        let data = await response.json();

        const customerId = String(customerData?._id || '').trim();
        const customerPhone = normalizePhone(customerData?.phoneNumber || '');
        const customerEmail = String(customerData?.email || '').trim().toLowerCase();

        data = data.filter((s) => {
          const serviceCustomerId = String(s.customerId || '').trim();
          const servicePhone = normalizePhone(
            s.customerSnapshot?.phoneNumber || s.phoneKey || ''
          );
          const serviceEmail = String(
            s.customerSnapshot?.email || ''
          ).trim().toLowerCase();

          const matchById =
            !!customerId &&
            !!serviceCustomerId &&
            serviceCustomerId === customerId;

          const matchByPhone =
            !!customerPhone &&
            !!servicePhone &&
            customerPhone === servicePhone;

          const matchByEmail =
            !!customerEmail &&
            !!serviceEmail &&
            serviceEmail === customerEmail;

          return matchById || matchByPhone || matchByEmail;
        });

        data.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
        setWorkshopServices(data);
      } catch (error) {
        console.error('Feil ved henting av workshop-services:', error);
        setWorkshopServices([]);
      } finally {
        setOneSectionLoading('workshopServices', false);
      }
    },
    []
  );

  const fetchOrders = useCallback(
    async (customerNumber) => {
      setOneSectionLoading('orders', true);

      try {
        const response = await fetch(`${API_BASE_URL}/orders?kundeid=${customerNumber}`);
        if (response.ok) {
          let ordersData = await response.json();

          if (butikkid) {
            ordersData = ordersData.filter(
              (o) => o.butikkid === parseInt(butikkid, 10)
            );
          }

          ordersData.sort(
            (a, b) => new Date(b.RegistrertDato || 0) - new Date(a.RegistrertDato || 0)
          );

          setOrders(ordersData);
        } else {
          setOrders([]);
        }
      } catch (error) {
        console.error('Feil ved henting av ordrer:', error);
        setOrders([]);
      } finally {
        setOneSectionLoading('orders', false);
      }
    },
    [butikkid]
  );

  const fetchSmsArchive = useCallback(async (phoneNumber) => {
    setOneSectionLoading('sms', true);

    try {
      const normalizedPhone = normalizePhone(phoneNumber);
      const response = await fetch(
        `${API_BASE_URL}/smsarchives?telefonnummer=${encodeURIComponent(normalizedPhone)}`
      );

      if (response.ok) {
        const smsData = await response.json();
        smsData.sort((a, b) => new Date(b.sendtDato || 0) - new Date(a.sendtDato || 0));
        setSmsArchive(smsData);
      } else {
        setSmsArchive([]);
      }
    } catch (error) {
      console.error('Feil ved henting av SMS-arkiv:', error);
      setSmsArchive([]);
    } finally {
      setOneSectionLoading('sms', false);
    }
  }, []);

  const fetchEmailArchive = useCallback(async (epost) => {
    if (!epost) return;
    setOneSectionLoading('email', true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/emailarchives?epost=${encodeURIComponent(epost)}`
      );

      if (response.ok) {
        const emailData = await response.json();
        emailData.sort((a, b) => new Date(b.sendtDato || 0) - new Date(a.sendtDato || 0));
        setEmailArchive(emailData);
      } else {
        setEmailArchive([]);
      }
    } catch (error) {
      console.error('Feil ved henting av e-postarkiv:', error);
      setEmailArchive([]);
    } finally {
      setOneSectionLoading('email', false);
    }
  }, []);

  const fetchOffers = useCallback(async (customerId) => {
    setOneSectionLoading('offers', true);

    try {
      const response = await fetch(`${API_BASE_URL}/offers`);

      if (response.ok) {
        const allOffers = await response.json();
        const filteredOffers = allOffers
          .filter((offer) => String(offer.kundeid || '') === String(customerId || ''))
          .sort(
            (a, b) =>
              new Date(b.registrertDato || 0) - new Date(a.registrertDato || 0)
          );

        setOffers(filteredOffers);
      } else {
        setOffers([]);
      }
    } catch (error) {
      console.error('Feil ved henting av tilbud:', error);
      setOffers([]);
    } finally {
      setOneSectionLoading('offers', false);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchCustomerData = async () => {
      try {
        setCustomerLoading(true);
        setError('');

        const response = await fetch(`${API_BASE_URL}/customers/${id}`);
        if (!response.ok) throw new Error('Kunde ble ikke funnet');

        const customerData = await response.json();
        if (!isMounted) return;

        setCustomer(customerData);
        setCustomerLoading(false);

        // Hent sykler
        if (customerData._id) {
          fetch(`${API_BASE_URL}/bikes?customerId=${encodeURIComponent(customerData._id)}`)
            .then((r) => r.json())
            .then((data) => { if (isMounted) setBikes(Array.isArray(data) ? data : []); })
            .catch(() => {});
        }

        // Prioriter bookingvarsel først
        fetchBookings(customerData);

        // Last resten i bakgrunnen
        fetchOrders(customerData.customerNumber);
        fetchLegacyServices(customerData.customerNumber);
        fetchWorkshopServices(customerData);
        fetchSmsArchive(customerData.phoneNumber);
        fetchEmailArchive(customerData.email);
        fetchOffers(customerData._id);
      } catch (error) {
        console.error('Feil ved henting av kunden:', error);
        if (!isMounted) return;
        setError('Kunne ikke hente kundedata.');
        setCustomerLoading(false);
      }
    };

    fetchCustomerData();

    return () => {
      isMounted = false;
    };
  }, [
    id,
    fetchBookings,
    fetchOrders,
    fetchLegacyServices,
    fetchWorkshopServices,
    fetchSmsArchive,
    fetchEmailArchive,
    fetchOffers,
  ]);

  const handleDeleteCustomer = async () => {
    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/customers/${customer._id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Feil ved sletting');
      navigate('/');
    } catch (e) {
      alert('Kunne ikke slette kunden: ' + e.message);
      setDeleting(false);
    }
  };

  // Hentes med fetch (ikke en vanlig lenke) slik at innloggingstokenet sendes med.
  const handleExportData = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/customers/${customer._id}/export`);
      if (!res.ok) throw new Error('Feil ved eksport');
      const disposition = res.headers.get('Content-Disposition') || '';
      const filename = disposition.match(/filename="([^"]+)"/)?.[1] || `gdpr-eksport-${customer._id}.json`;
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Kunne ikke laste ned data: ' + e.message);
    }
  };

  // Sender bare en lenke til Din side, der kunden logger inn og ser dataene (src/gdpr/innsyn.js).
  const sendInnsynslenke = async () => {
    setInnsyn('sender');
    try {
      const res = await fetch(`${API_BASE_URL}/customers/${customer._id}/innsyn`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ansatt: Cookies.get('selectedEmployee') || '' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Kunne ikke sende lenken');
      setInnsyn(data);
    } catch (e) {
      setInnsyn({ feil: e.message });
    }
  };

  // Dekkhotell og sykkelhotell (samme ruter som før DekkhotellKort/SykkelhotellKort brukte).
  const kundeId = customer?._id;
  useEffect(() => {
    if (!kundeId) return undefined;
    let aktiv = true;
    setOneSectionLoading('hotell', true);
    const hent = (sti) => fetch(`${API_BASE_URL}${sti}`).then((r) => (r.ok ? r.json() : [])).catch(() => []);
    Promise.all([hent(`/dekkhotell/kunde/${kundeId}`), hent(`/sykkelhotell/kunde/${kundeId}`)]).then(([d, s]) => {
      if (!aktiv) return;
      setDekkhotell(Array.isArray(d) ? d : []);
      setSykkelhotell(Array.isArray(s) ? s : []);
      setOneSectionLoading('hotell', false);
    });
    return () => { aktiv = false; };
  }, [kundeId]);

  // Alt kunden har hos oss, som like rader: { key, type, tittel, detaljer, status, dato, aktiv, til }.
  const saker = useMemo(() => {
    const liste = [];

    for (const b of bookings) {
      const produkter = (b.items || []).map((p) => p?.produktmerke || p?.produktnavn).filter(Boolean);
      liste.push({
        key: `booking-${b.id}`,
        type: 'booking',
        tittel: produkter.length ? produkter.join(', ') : 'Booking',
        detaljer: [b.storeName, formatNOK(b.totalPrice), formatMinutes(b.totalMinutes), b.estimatedFinish && `ferdig ${b.estimatedFinish}`]
          .filter(Boolean)
          .join(' · '),
        status: storForbokstav(b.status),
        dato: tilDato(b.createdAt),
        aktiv: isActiveBookingStatus(b.status),
        til: `/booking/${b.id}`,
      });
    }

    for (const s of workshopServices) {
      const st = String(s.status || '').toLowerCase();
      const kategori = normalizeKategori(s.kategori, s.category, s.produkt?.kategori, s.produkt?.produktkategori);
      liste.push({
        key: `service-${s._id}`,
        type: 'service',
        tittel: s.produkt?.produktmerke || s.produkt?.navn || 'Produkt',
        detaljer: [s.serviceRef, KATEGORI_NAVN[kategori.toLowerCase()] || kategori].filter(Boolean).join(' · '),
        status: storForbokstav(s.status),
        dato: tilDato(s.createdAt),
        aktiv: st !== 'avsluttet' && st !== 'kansellert',
        til: `/workshop/${s._id}`,
      });
    }

    for (const s of legacyServices) {
      liste.push({
        key: `trykk-${s._id}`,
        type: s.servicetype === 'Tekstiltrykking' || !s.servicetype ? 'trykk' : 'service',
        tittel: [s.Varemerke, s.Produkt].filter(Boolean).join(' ') || s.servicetype || 'Tjeneste',
        detaljer: [s.serviceid && `Nr. ${s.serviceid}`, s.servicetype !== 'Tekstiltrykking' && s.servicetype, s.Størrelse, s.Farge, s.Beskrivelse]
          .filter(Boolean)
          .join(' · '),
        status: storForbokstav(s.status),
        dato: tilDato(s.registrertDato),
        aktiv: s.status !== 'Avsluttet',
        til: gammelTjenesteSti(s),
      });
    }

    for (const o of orders) {
      liste.push({
        key: `ordre-${o._id}`,
        type: 'ordre',
        tittel: [o.Varemerke, o.Produkt].filter(Boolean).join(' ') || 'Ordre',
        detaljer: [o.ordreid && `Nr. ${o.ordreid}`, o.Størrelse, o.Farge, o.Kommentar].filter(Boolean).join(' · '),
        status: ordreStatusTekst(o.Status),
        dato: tilDato(o.RegistrertDato),
        aktiv: o.Status !== 'Avsluttet',
        til: `/order-details/${o._id}`,
      });
    }

    for (const t of offers) {
      const st = String(t.status || '').toLowerCase();
      const antall = Array.isArray(t.produkter) ? t.produkter.length : 0;
      liste.push({
        key: `tilbud-${t._id}`,
        type: 'tilbud',
        tittel: antall ? `Pristilbud, ${antall} ${antall === 1 ? 'produkt' : 'produkter'}` : 'Pristilbud',
        detaljer: t.ansatt ? `Laget av ${t.ansatt}` : '',
        status: st === 'avslatt' ? 'Avslått' : storForbokstav(t.status),
        dato: tilDato(t.registrertDato),
        aktiv: !['godkjent', 'avslatt', 'kansellert', 'erstattet', 'avsluttet'].includes(st),
        til: `/offer-details/${t._id}`,
      });
    }

    for (const p of dekkhotell) {
      const lagret = p.status === 'lagret';
      liste.push({
        key: `dekkhotell-${p._id}`,
        type: 'dekkhotell',
        tittel: lagret ? `${SETT_LABEL[p.lagret?.sett] || 'Dekk'} på lager` : 'Utlevert',
        detaljer: [p.nr, lagret && settTekst({ ...p.lagret, sett: null }), lagret && `Plass: ${stedTekst(p) || 'ikke satt'}`].filter(Boolean).join(' · '),
        status: lagret ? 'På lager' : 'Utlevert',
        dato: tilDato(lagret ? p.createdAt : p.avsluttetDato || p.createdAt),
        aktiv: lagret,
        til: `/dekkhotell/${p._id}`,
      });
    }

    for (const p of sykkelhotell) {
      liste.push({
        key: `sykkelhotell-${p._id}`,
        type: 'sykkelhotell',
        tittel: sykkelTekst(p) || 'Sykkel',
        detaljer: [p.nr, p.sesong && `Sesong ${p.sesong}`].filter(Boolean).join(' · '),
        status: SYKKELHOTELL_STATUS[p.status] || storForbokstav(p.status),
        dato: tilDato(p.status === 'utlevert' ? p.avsluttetDato || p.createdAt : p.createdAt),
        aktiv: p.status !== 'utlevert',
        til: `/sykkelhotell/${p._id}`,
      });
    }

    return liste.sort((a, b) => (b.dato || 0) - (a.dato || 0));
  }, [bookings, workshopServices, legacyServices, orders, offers, dekkhotell, sykkelhotell]);

  const aktive = saker.filter((s) => s.aktiv);
  const avsluttede = saker.filter((s) => !s.aktiv);
  const avsluttetTyper = Object.keys(TYPER)
    .map((type) => ({ type, antall: avsluttede.filter((s) => s.type === type).length }))
    .filter((t) => t.antall > 0);
  const valgtAvsluttetType = avsluttetTyper.some((t) => t.type === avsluttetType) ? avsluttetType : 'alle';
  const avsluttedeFiltrert = valgtAvsluttetType === 'alle' ? avsluttede : avsluttede.filter((s) => s.type === valgtAvsluttetType);
  const lasterSaker = ['bookings', 'workshopServices', 'legacyServices', 'orders', 'offers', 'hotell'].some((k) => sectionLoading[k]);

  if (customerLoading) {
    return <p className="mt-4 text-center text-slate-600">Laster kundeinformasjon …</p>;
  }

  if (error) {
    return <p role="alert" className="mt-4 text-center text-red-700">{error}</p>;
  }

  if (!customer) {
    return <p className="mt-4 text-center text-red-700">Leter etter kunde</p>;
  }

  const smsForCustomer = smsArchive.filter(
    (sms) => normalizePhone(sms.telefonnummer) === normalizePhone(customer.phoneNumber)
  );

  // SMS og e-post i én liste, nyeste først.
  const meldinger = [
    ...smsForCustomer.map((sms) => ({ key: `sms-${sms._id}`, type: 'SMS', dato: tilDato(sms.sendtDato), sms })),
    ...emailArchive.map((mail) => ({ key: `epost-${mail._id}`, type: 'E-post', dato: tilDato(mail.sendtDato), mail })),
  ].sort((a, b) => (b.dato || 0) - (a.dato || 0));
  const visteMeldinger = visAlleMeldinger ? meldinger : meldinger.slice(0, 1);
  const lasterMeldinger = sectionLoading.sms || sectionLoading.email;

  const inaktiv = (customer.status || '').toLowerCase() === 'inaktiv';
  const navn = [customer.firstName, customer.lastName].filter(Boolean).join(' ') || 'Uten navn';

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-10">
      <Link to={FORSIDE} className="inline-flex items-center gap-2 rounded-lg px-2 py-1.5 font-medium text-indigo-700 hover:bg-indigo-50">
        <Ikon navn="tilbake" className="w-5 h-5" /> Tilbake til kontrollpanelet
      </Link>

      {/* Kunden: navn, status og kontaktinfo tett sammen (vinnsel, 30.10). DB-ID er fjernet. */}
      <header className="space-y-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${FARGER.kunde.ikon}`}>
            <Ikon navn="person" className="w-5 h-5" />
          </span>
          <h1 className="min-w-0 text-xl font-bold leading-tight text-slate-900 break-words">{navn}</h1>
          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${inaktiv ? 'bg-slate-200 text-slate-700' : 'bg-emerald-100 text-emerald-900'}`}>
            {inaktiv ? 'Inaktiv' : 'Aktiv'}
          </span>
          {customer.customerNumber && <span className="text-sm text-slate-700">Kundenr. {customer.customerNumber}</span>}
          <Link
            to={`/edit-customer/${customer._id}`}
            className="ml-auto inline-flex min-h-[2.25rem] items-center gap-1.5 rounded-lg border-2 border-slate-300 bg-white px-2.5 py-1 text-sm font-semibold text-slate-800 hover:bg-slate-100 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
          >
            <Ikon navn="blyant" className="w-4 h-4" /> Endre
          </Link>
        </div>

        <dl className="flex flex-wrap gap-x-8 gap-y-1.5">
          <div className="min-w-0">
            <dt className="text-xs text-slate-600">Telefon</dt>
            <dd className="font-mono font-semibold text-slate-900">{customer.normalizedPhone || customer.phoneNumber}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-slate-600">E-post</dt>
            <dd className="font-semibold text-slate-900 [overflow-wrap:anywhere]">{customer.email || 'Ikke registrert'}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-xs text-slate-600">Registrert</dt>
            <dd className="text-slate-900">{fmtDateShort(customer.registrationDate) || 'Ukjent'}</dd>
          </div>
          {customer.lastModified && (
            <div className="min-w-0">
              <dt className="text-xs text-slate-600">Endret</dt>
              <dd className="text-slate-900">{fmtDateShort(customer.lastModified)}</dd>
            </div>
          )}
        </dl>

        <div className="flex flex-wrap items-center gap-x-8 gap-y-2">
          {bikes.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-600">Sykkelkort</h2>
              <ul className="flex flex-wrap gap-1.5">
                {bikes.map((b) => (
                  <li key={b._id}>
                    <Link
                      to={`/sykkelkort/${b._id}`}
                      className="inline-flex min-h-[2.25rem] items-center gap-2 rounded-lg border border-emerald-200 bg-white px-2.5 py-1 text-sm font-semibold text-slate-900 hover:bg-emerald-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
                    >
                      <Ikon navn="idkort" className="w-5 h-5 text-emerald-700" />
                      {[b.merke, b.modell, b.årsmodell && `(${b.årsmodell})`].filter(Boolean).join(' ') || 'Sykkel'}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* Reservasjon mot servicepåminnelser på SMS. Komponenten har mb-4 (også i kundeportalen); ikke her. */}
          <div className="[&>div]:mb-0">
            <Servicepaminnelse customerId={customer._id} />
          </div>
        </div>
      </header>

      {/* Bookingvarsel – prioritert, som før */}
      {sectionLoading.bookings ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-3">
          <p className="font-medium text-amber-900">Sjekker om kunden har aktiv booking …</p>
        </div>
      ) : activeBookingBookings.length > 0 ? (
        <div role="status" className="rounded-2xl border-2 border-amber-400 bg-amber-50 p-3 shadow-sm">
          <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-2">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-500 text-amber-950">
                <Ikon navn="kalender" className="w-7 h-7" />
              </span>
              <div>
                <h2 className="text-base font-bold text-amber-950">Kunden har allerede booket servicetid</h2>
                <p className="mt-1 text-amber-900">
                  Denne kunden har {activeBookingBookings.length}{' '}
                  {activeBookingBookings.length === 1 ? 'aktiv booking' : 'aktive bookinger'}. Gå direkte til bookingen for
                  mottak av service, så du unngår dobbeltregistrering.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {activeBookingBookings.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => navigate(`/booking/${b.id}`)}
                  className="min-h-[2.25rem] rounded-xl bg-amber-700 px-3 py-1.5 font-semibold text-white hover:bg-amber-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
                >
                  Åpne booking {b.createdAtText ? `(${b.createdAtText})` : ''}
                </button>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      {/* Knappene, med samme navn som på kontrollpanelet (vinnsel, 30.10): to rader à fire,
          registrering øverst og lagring/tilbud under. */}
      <section aria-labelledby="kd-gjor" className="space-y-2">
        <h2 id="kd-gjor" className="text-lg font-bold text-slate-900">Hva skal du gjøre?</h2>
        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <li><Handling ikon="skiftenokkel" farge="verksted" tittel="Registrer service" onClick={goToBmod} /></li>
          <li><Handling ikon="kvittering" farge="betaling" tittel="Registrer ordre" to={`/create-order/${customer.customerNumber}`} /></li>
          <li><Handling ikon="genser" farge="trykk" tittel="Registrer trykk" onClick={() => navigate(`/create-service/${customer.customerNumber}`)} /></li>
          <li><Handling ikon="send" farge="kunde" tittel="Send SMS" to="/sendsms" state={{ customer }} /></li>
          <li><Handling ikon="snofnugg" farge="lager" tittel="Registrer skiplukk" to={`/create-order/${customer.customerNumber}?type=skiplukk`} /></li>
          <li><Handling ikon="dokument" farge="verksted" tittel={creatingOffer ? 'Oppretter …' : 'Lag pristilbud'} onClick={createOffer} disabled={creatingOffer} /></li>
          <li><Handling ikon="dekk" farge="lager" tittel={"Legg inn i dekk\u00ADhotell"} to={`/dekkhotell?kunde=${customer._id}`} /></li>
          <li><Handling ikon="hotell" farge="lager" tittel={"Legg inn i sykkel\u00ADhotell"} to={`/sykkelhotell?kunde=${customer._id}`} /></li>
        </ul>
      </section>

      {/* Aktive nå */}
      <Seksjon id="kd-aktive" tittel="Aktive nå" antall={lasterSaker && !aktive.length ? null : aktive.length}>
        {aktive.length > 0 ? (
          <ul className="divide-y divide-slate-200 overflow-hidden rounded-2xl border-2 border-slate-200 bg-white">
            {aktive.map((s) => <SakRad key={s.key} sak={s} />)}
          </ul>
        ) : (
          <Tom>{lasterSaker ? 'Henter bookinger, servicer, ordrer og tilbud …' : 'Kunden har ingenting aktivt nå.'}</Tom>
        )}
        {lasterSaker && aktive.length > 0 && <p className="text-sm text-slate-600">Henter resten …</p>}
      </Seksjon>

      {/* Avsluttet */}
      <Seksjon id="kd-avsluttet" tittel="Avsluttet" antall={lasterSaker && !avsluttede.length ? null : avsluttede.length}>
        {avsluttetTyper.length > 1 && (
          <div role="group" aria-label="Vis type" className="flex flex-wrap gap-2">
            {[{ type: 'alle', antall: avsluttede.length }, ...avsluttetTyper].map((t) => (
              <button
                key={t.type}
                type="button"
                aria-pressed={valgtAvsluttetType === t.type}
                onClick={() => { setAvsluttetType(t.type); setVisAntallAvsluttet(10); }}
                className={`min-h-[2.25rem] rounded-xl border-2 px-3.5 py-1.5 font-semibold transition focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50 ${
                  valgtAvsluttetType === t.type ? 'border-indigo-700 bg-indigo-700 text-white' : 'border-slate-200 bg-white text-slate-800 hover:bg-slate-50'
                }`}
              >
                {t.type === 'alle' ? 'Alle' : TYPER[t.type].navn} ({t.antall})
              </button>
            ))}
          </div>
        )}
        {avsluttedeFiltrert.length > 0 ? (
          <>
            <ul className="divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200 bg-white">
              {avsluttedeFiltrert.slice(0, visAntallAvsluttet).map((s) => <SakRad key={s.key} sak={s} />)}
            </ul>
            {avsluttedeFiltrert.length > visAntallAvsluttet && (
              <button
                type="button"
                onClick={() => setVisAntallAvsluttet((n) => n + 20)}
                className="min-h-[2.25rem] rounded-xl border-2 border-slate-300 bg-white px-3 py-1.5 font-semibold text-slate-800 hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
              >
                Vis flere ({avsluttedeFiltrert.length - visAntallAvsluttet} til)
              </button>
            )}
          </>
        ) : (
          <Tom>{lasterSaker ? 'Henter …' : 'Ingenting avsluttet ennå.'}</Tom>
        )}
      </Seksjon>

      {/* SMS og e-post */}
      <Seksjon
        id="kd-meldinger"
        tittel="SMS og e-post"
        antall={lasterMeldinger && !meldinger.length ? null : meldinger.length}
        hoyre={meldinger.length > 1 && (
          <button
            type="button"
            onClick={() => setVisAlleMeldinger((v) => !v)}
            aria-expanded={visAlleMeldinger}
            aria-controls="kd-meldinger-tabell"
            className="inline-flex min-h-[2.25rem] items-center gap-2 rounded-xl border-2 border-slate-300 bg-white px-3 py-1.5 font-semibold text-slate-800 hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
          >
            {visAlleMeldinger ? 'Vis bare siste' : `Vis alle ${meldinger.length}`}
            <Ikon navn="ned" className={`w-5 h-5 transition-transform ${visAlleMeldinger ? 'rotate-180' : ''}`} />
          </button>
        )}
      >
        {meldinger.length > 0 ? (
          <div tabIndex={0} role="region" aria-label="SMS og e-post, kan rulles sidelengs" className="overflow-x-auto rounded-2xl border border-slate-200 bg-white focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50">
            <table id="kd-meldinger-tabell" className="min-w-full text-left">
              <caption className="sr-only">{visAlleMeldinger ? 'Alle SMS-er og e-poster til kunden' : 'Siste SMS eller e-post til kunden'}</caption>
              <thead className="bg-slate-100 text-sm font-semibold text-slate-700">
                <tr>
                  <th scope="col" className="px-3 py-1.5 whitespace-nowrap">Sendt</th>
                  <th scope="col" className="hidden px-3 py-1.5 sm:table-cell">Type</th>
                  <th scope="col" className="px-3 py-1.5">Melding</th>
                  <th scope="col" className="px-3 py-1.5">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 text-sm text-slate-800">
                {visteMeldinger.map((m) => (
                  <tr key={m.key} className="align-top">
                    <td className="px-3 py-1.5">
                      <span className="block whitespace-nowrap">{m.dato ? formatDateTime(m.dato) : 'Ukjent dato'}</span>
                      {/* På smal skjerm står typen her, så meldingen får plassen. */}
                      <span className="mt-1 block sm:hidden"><MeldingType type={m.type} /></span>
                    </td>
                    <td className="hidden px-3 py-1.5 sm:table-cell"><MeldingType type={m.type} /></td>
                    <td className="min-w-[12rem] px-3 py-1.5 whitespace-pre-line [overflow-wrap:anywhere]">
                      {m.sms ? m.sms.meldingstekst : (
                        <>
                          <span className="font-semibold">{m.mail.emne || 'Uten emne'}</span>
                          {m.mail.emailKind && <span className="block text-slate-600">{m.mail.emailKind}</span>}
                        </>
                      )}
                    </td>
                    <td className="px-3 py-1.5">{m.sms ? <SmsStatusMerke sms={m.sms} visGrunn /> : <span className="text-slate-600">Sendt</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Tom>
            {lasterMeldinger ? 'Henter SMS og e-post …' : customer.email ? 'Ingen SMS-er eller e-poster sendt til kunden.' : 'Ingen SMS-er sendt til kunden. Kunden har ingen e-postadresse.'}
          </Tom>
        )}
      </Seksjon>

      {/* GDPR */}
      <section aria-labelledby="kd-gdpr" className="space-y-2 border-t border-slate-200 pt-6">
        <h2 id="kd-gdpr" className="text-sm font-semibold uppercase tracking-wide text-slate-600">Personvern</h2>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleExportData}
            className="min-h-[2.25rem] rounded-xl border-2 border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
          >
            Last ned data (GDPR)
          </button>
          <button
            type="button"
            onClick={() => setInnsyn('sporsmal')}
            aria-expanded={innsyn === 'sporsmal'}
            className="min-h-[2.25rem] rounded-xl border-2 border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
          >
            Send GDPR-data til kunde
          </button>
          {erLeder && (
            <button
              type="button"
              onClick={() => setShowDeleteModal(true)}
              className="min-h-[2.25rem] rounded-xl border-2 border-red-300 bg-white px-3 py-1.5 font-medium text-red-700 hover:bg-red-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
            >
              Slett kunde
            </button>
          )}
        </div>
        {(innsyn === 'sporsmal' || innsyn === 'sender') && (
          <div className="rounded-xl border-2 border-slate-200 bg-white p-3 text-slate-800">
            <p>
              Kunden får en lenke til Din side {customer.phoneNumber ? 'på SMS' : customer.email ? 'på e-post' : ''}. Der logger kunden
              inn med en kode, og kan se dataene, laste dem ned eller få dem på e-post. Selve dataene sendes ikke.
            </p>
            <p className="mt-2 text-sm text-slate-600">Bruk dette når kunden har bedt om å få se opplysningene sine. Forespørselen logges.</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={sendInnsynslenke}
                disabled={innsyn === 'sender' || (!customer.phoneNumber && !customer.email)}
                className="min-h-[2.25rem] rounded-xl bg-indigo-700 px-3 py-1.5 font-semibold text-white hover:bg-indigo-800 disabled:opacity-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
              >
                {innsyn === 'sender' ? 'Sender …' : 'Send lenken'}
              </button>
              <button
                type="button"
                onClick={() => setInnsyn(null)}
                className="min-h-[2.25rem] rounded-xl border-2 border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
              >
                Avbryt
              </button>
            </div>
            {!customer.phoneNumber && !customer.email && (
              <p className="mt-2 text-sm text-amber-900">Kunden har verken mobil eller e-post. Legg til en av dem først.</p>
            )}
          </div>
        )}
        <p role="status" className="text-sm text-slate-800">
          {innsyn?.til && `Lenken er sendt ${innsyn.kanal === 'sms' ? 'på SMS' : 'på e-post'} til ${innsyn.til}.`}
        </p>
        {innsyn?.feil && <p role="alert" className="text-sm text-red-700">{innsyn.feil}</p>}
        {!erLeder && (
          <p className="text-sm text-slate-600">Bare en leder kan slette kunden. Velg en leder øverst, eller si fra til en leder.</p>
        )}
      </section>

      {/* Slett-modal */}
      {showDeleteModal && erLeder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-3">
          <div role="dialog" aria-modal="true" aria-labelledby="kd-slett-tittel" className="max-h-full w-full max-w-md overflow-y-auto rounded-xl bg-white p-5 shadow-xl">
            <h3 id="kd-slett-tittel" className="mb-2 text-base font-bold text-gray-900">Slett kundekort</h3>
            <p className="text-sm text-gray-600 mb-4">
              Dette sletter <strong>{customer.firstName} {customer.lastName}</strong> etter GDPR.
              Handlingen kan ikke angres.
            </p>
            <div className="rounded-lg bg-red-50 border border-red-200 p-2.5 mb-3 text-sm text-red-800">
              <p className="font-semibold mb-1">Slettes:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>Selve kundekortet</li>
                <li>{bookings.length} bookinger og tilbud som ikke er godkjent, med bildene</li>
                <li>Chat og akuttforespørsler fra kundens telefon/e-post</li>
                <li>SMS- og e-posthistorikk</li>
                <li>Dekkhotellet (lever ut dekk som ligger på lager først)</li>
                <li>Sykkelhotellet (lever ut sykkelen først hvis den står inne)</li>
              </ul>
            </div>
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5 mb-5 text-sm text-amber-900">
              <p className="font-semibold mb-1">Sperres og oppbevares i 5 år (bokføringsloven):</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li>{workshopServices.length} servicer, med salgsbilag</li>
                <li>{bikes.length} sykkelkort, ordrer og godkjente tilbud</li>
              </ul>
              <p className="mt-1">
                Sperrede poster vises ikke lenger i systemet, men kan slås opp under Verksted → Sperrede poster.
                Bildene deres slettes når oppbevaringstiden er ute.
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                autoFocus
                className="px-3 py-1.5 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
              >
                Avbryt
              </button>
              <button
                type="button"
                onClick={handleDeleteCustomer}
                disabled={deleting}
                className="px-3 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50"
              >
                {deleting ? 'Sletter…' : 'Slett permanent'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomerDetails;
